import { Component, Input, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';
import { DecimalConfigService } from 'app/services/decimal-config.service';

/**
 * Decimal Cell Renderer Component
 *
 * Reusable cell renderer for displaying decimal/numeric values with formatting.
 * Applies the DecimalFormatPipe for consistent number display across the application.
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   field: 'total',
 *   cellRenderer: DecimalCellRendererComponent
 * }
 * ```
 */
@Component({
  selector: 'app-decimal-cell-renderer',
  standalone: true,
  imports: [CommonModule, DecimalPipe],
  template: `
    <div >
      {{ displayValue }}
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DecimalCellRendererComponent implements CellRenderer<any, number> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<any, number>;

  /**
   * Injected services
   */
  private readonly configService = inject(DecimalConfigService);
  private readonly decimalPipe = new DecimalPipe('en-US');

  /**
   * Get the formatted display value
   */
  get displayValue(): string {
    const value = this.params?.value;

    // Handle null/undefined values
    if (value === null || value === undefined) {
      return '-';
    }

    // Apply decimal formatting using the same logic as DecimalFormatPipe
    const decimals = this.configService.getDecimals();
    return this.decimalPipe.transform(value, `1.${decimals}-${decimals}`) ?? '-';
  }
}
